/*global wx*/

import React, { useCallback, useEffect, useState } from "react";
import weui from "weui.js";
import { payService } from "../services";
import { getAxiosError, isWechat_iOS } from "../utils";

/**
 * 微信扫码读取表号 HOC
 * 为组件注入 scanCodeEnabled 属性和 scanCode 方法, 之后可在组件内使用 this.props.scanCode(...) 来调用微信扫码功能读取表号.
 * @param Component
 * @returns {function(*)}
 */
export const withWechatScanCode = (Component) => {
  const HOC = (props) => {
    const [ready, setReady] = useState(false);

    const wxConfig = useCallback((data) => {
      wx.config({
        debug: false,
        appId: data.appId,
        timestamp: data.timestamp,
        nonceStr: data.nonceStr,
        signature: data.signature,
        jsApiList: ["checkJsApi", "scanQRCode"]
      });
      
      wx.error(res => {
        setReady(false);
      });
      
      wx.ready(() => {
        wx.checkJsApi({
          jsApiList: ["scanQRCode"],
          success: res => setReady(res.checkResult && !!res.checkResult.scanQRCode),
          fail: res => setReady(false)
        });
      });
    }, [setReady]);
    
    const loadJsSdkData = useCallback(async () => {
      try {
        let url = window.location.href;
        if (isWechat_iOS()) {
          url = window.entryUrl;
        }

        const res = await payService.getWechatJsSdkData(url);
        wxConfig(res.data);
      } catch (err) {
        // weui.topTips(getAxiosError(err));
        setReady(false);
      }
    }, [setReady, wxConfig]);
    
    useEffect(() => {
      loadJsSdkData();
    }, [loadJsSdkData]);
    
    const wechatScanCode = useCallback((success, fail) => {
      const invokeFail = err => typeof fail === "function" && fail(err);

      if (!ready) {
        invokeFail("扫码功能暂不可用, 请稍候重试.");
        return;
      }
      
      const invalidCode = () => invokeFail("您扫描的表计二维码(条码)无效!");

      wx.scanQRCode({
        needResult: 1, // 默认为 0, 扫描结果由微信处理, 1 则直接返回扫描结果
        scanType: ["qrCode", "barCode"], // barCode, qrCode 测试发现微信无法描述出条码内容, 所以暂不启用
        success: res => {
          // 清华联表计二维码数据格式: 868221048509957,085201906201296,016200723-20002924
          // 第一部分为 NB 通讯模块的 IMEI 编号, 第二部分为 SN 号, 第三部分为仪表表号
          // 清华联表计条码数据格式: CODE_128,016200723-20002924
          // 第一部分为条码格式, 第二部分为仪表表号
          // 仪表表号又以 `-` 分隔为两部分, 第二部分为录入到清华联系统内的实际表号
          // 另外, 清华联系统内表号要求 12 位, 不足需要左边补 0
          // 2021-12-09 更新
          // 清华联新二维码数据格式: 143210408-30005975
          let codes = (res.resultStr || "").split(",");
          if (codes.length < 1) {
            invalidCode();
            return;
          }
          
          let meterNo = codes[codes.length - 1];
          codes = meterNo.split("-");
          if (codes.length < 2) {
            invalidCode();
            return;
          }

          meterNo = codes[1];
          if (!meterNo) {
            invalidCode();
            return;
          }
          
          if (typeof success === "function") {
            success(meterNo);  
          }
        },
        fail: res => {
          invokeFail(res.errMsg);
        }
      });
    }, [ready]);

    return <Component scanCodeEnabled={ ready } scanCode={ wechatScanCode } { ...props } />;
  };

  HOC.displayName = `WithWechatScanCode(${Component.displayName || Component.name || "Component"})`;

  return HOC;
};
